import { useMemo, useState } from "react";

import { stripTagPrefix } from "./tagVerificationInput";

export type SortDir = "asc" | "desc";

export function normalizeText(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function compareValues(left: unknown, right: unknown): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left).localeCompare(String(right), "pl", {
    numeric: true,
    sensitivity: "base",
  });
}

export type TableControlsOptions<T> = {
  rows: T[];
  getValue: (row: T, key: string) => unknown;
  getSortValue?: (row: T, key: string) => unknown;
  defaultSortKey?: string | null;
  defaultSortDir?: SortDir;
  initialFilters?: Record<string, string>;
};

export function useTableControls<T>({
  rows,
  getValue,
  getSortValue,
  defaultSortKey = null,
  defaultSortDir = "asc",
  initialFilters = {},
}: TableControlsOptions<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir);
  const [filters, setFilters] = useState<Record<string, string>>(initialFilters);

  function toggleSort(key: string) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  function setFilter(key: string, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function clearFilters() {
    setFilters({});
  }

  const view = useMemo(() => {
    const filtered = rows.filter((row) =>
      Object.entries(filters).every(([key, raw]) => {
        const query = normalizeText(raw);
        if (!query) {
          return true;
        }
        const normalizedValue = normalizeText(getValue(row, key));
        if (key === "status" || key === "sex") {
          return normalizedValue === query;
        }
        const tagField =
          key === "tag" || key === "mother_tag" || key === "father_tag" || key === "official_tag";
        const comparable = tagField ? normalizedValue.replace(/[\s-]/g, "") : normalizedValue;
        const comparableQuery = tagField ? query.replace(/[\s-]/g, "") : query;
        if (comparable.includes(comparableQuery)) {
          return true;
        }
        // Official ear-tag fields only — not working_tag / activity numbers.
        if (tagField) {
          const stripped = stripTagPrefix(comparableQuery);
          return (
            Boolean(stripped) && stripped !== comparableQuery && comparable.includes(stripped)
          );
        }
        return false;
      }),
    );
    if (!sortKey) {
      return filtered;
    }
    const direction = sortDir === "asc" ? 1 : -1;
    const sortOf = getSortValue ?? getValue;
    return [...filtered].sort(
      (left, right) =>
        compareValues(sortOf(left, sortKey), sortOf(right, sortKey)) * direction,
    );
  }, [filters, getSortValue, getValue, rows, sortDir, sortKey]);

  return {
    view,
    sortKey,
    sortDir,
    filters,
    toggleSort,
    setFilter,
    clearFilters,
    hasActiveFilters: Object.values(filters).some((value) => Boolean(String(value || "").trim())),
  };
}
